const { debounce, getElement } = window.BAO.Utils

class CartItem extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this._loading = false
    this.sections = this.inPushCart
      ? window.BAO.sectionRenderer.pushCartSections
      : this.onCartPage
        ? window.BAO.sectionRenderer.cartSections
        : []

    this.tryToRevealAddon()
  }

  setupListeners () {
    super.setupListeners()

    this.onQuantityChange = this.onQuantityChange.bind(this)
    this.debouncedOnQuantityChange = debounce(event => {
      this.onQuantityChange(event)
    }, 500)

    if (this.els.remove.exists) {
      this.removeLineItem = this.removeLineItem.bind(this)

      this.els.remove.elements.forEach(remove => {
        this.listeners.add(remove, 'click', this.removeLineItem)
      })
    }

    this.listeners.add(
      this,
      window.BAO.EVENTS.QUANTITY_ADJUSTER.CHANGE,
      this.debouncedOnQuantityChange,
    )

    if (this.els['addon-dismiss'].exists) {
      this.listeners.add(this.els['addon-dismiss'].element, 'click', () =>
        this.dismissAddon(),
      )
    }
  }

  /**
   * @param {MouseEvent} e
   *
   * @returns {void}
   */
  async removeLineItem (e) {
    e.preventDefault()

    const parentElement = getElement('push-cart') ?? getElement('cart-items')

    this.els.content?.element.setAttribute('aria-hidden', true)
    this.els.removed?.element.setAttribute('aria-hidden', false)
    this.classList.add('psh-CartItem-removed')
    this.els.wishlist?.element.setAttribute('aria-hidden', false)

    this.loading = true

    // TODO: If added via a stack this seems to remove every gifting option on that stack
    const state = await this.removeItemsFromCart()

    const items = { 'push-cart-items': state.sections['push-cart-items'] }
    delete state.sections['push-cart-items']
    this.renderSections(state.sections)

    this.loading = false

    if (this.els.cancel.exists && this.els.add.exists) {
      // push-cart-items is withheld above to keep the wishlist prompt on screen,
      // so its GWP config is stale in the DOM. Hand the fresh markup to the GWP
      // engine so a gift that is no longer earned is removed without waiting for
      // the prompt to be dismissed.
      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.GWP_REEVALUATE, {
        pushCartItemsHtml: items['push-cart-items'],
      })

      this.listeners.add(this.els.cancel?.element, 'click', () => {
        parentElement.refresh()
      })
      this.listeners.add(this.els.add?.element, 'click', () => {
        const clonedCartItem = this.parentNode.cloneNode(true)
        parentElement.refresh()

        const cartItemsContainer = document.getElementById('push-cart-items')
        const cartItemCancelButton = clonedCartItem.querySelector(
          '[data-cart-item-el="cancel"]',
        )
        const cartItemLinks = clonedCartItem.querySelectorAll('a')
        const cartItemAddedTitle = clonedCartItem.querySelector(
          '[data-cart-item-el="removed"] .psh-CartItem_Title',
        )
        const cartItemAddedText = clonedCartItem.querySelector(
          '[data-cart-item-el="removed"] .psh-CartItem_Text',
        )
        const cartItemWishlistButton = clonedCartItem.querySelector(
          '[data-wishlist-button-add]',
        )

        if (cartItemCancelButton) {
          cartItemCancelButton.parentNode.removeChild(cartItemCancelButton)
        }
        if (cartItemLinks.length > 0) {
          cartItemLinks.forEach(link => {
            link.href = '/pages/wishlist'
          })
        }
        if (cartItemAddedTitle) {
          cartItemAddedTitle.textContent = window.pushcartStrings.added_wishlist
        }
        if (cartItemAddedText) {
          cartItemAddedText.textContent = window.pushcartStrings.saved_message
        }
        if (cartItemWishlistButton) {
          cartItemWishlistButton.removeAttribute('data-wishlist-button-add')
          cartItemWishlistButton.removeAttribute('data-cart-item-el')
          cartItemWishlistButton.setAttribute(
            'onclick',
            "window.location.href='/pages/wishlist';",
          )
          cartItemWishlistButton.removeAttribute('data-product-id')
          cartItemWishlistButton.removeAttribute('data-variant-id')
          cartItemWishlistButton.removeAttribute('data-product-url')
          cartItemWishlistButton.removeAttribute('data-variant-url')
          cartItemWishlistButton.removeAttribute(
            'data-wishlist-drawer-external-el',
          )
          cartItemWishlistButton.removeAttribute('data-wishlist-id')
          cartItemWishlistButton.removeAttribute('data-drawers-trigger')
          cartItemWishlistButton.textContent =
            window.pushcartStrings.wishlist_button
        }

        const wishlistDrawer = document.querySelector(
          '[data-wishlist-drawer-el="drawer"',
        )

        wishlistDrawer.setAttribute('data-from-cart', true)

        cartItemsContainer.prepend(clonedCartItem)
      })
    } else {
      if ('sections' in state) this.renderSections(items)
    }
  }

  async removeItemsFromCart () {
    if (this.hasGifting && !this.isBespoke) {
      return window.BAO.Cart.removeItems([this.dataset.key], {
        sections: this.mappedSections,
      })
    }

    if (this.isBespoke && !this.hasGifting) {
      return window.BAO.Cart.removeItems(
        [this.dataset.key, ...this.bespokeKeys],
        {
          sections: this.mappedSections,
        },
      )
    }

    if (this.isBespoke && this.hasGifting) {
      return window.BAO.Cart.removeItems(
        [this.dataset.key, ...this.bespokeKeys],
        {
          sections: this.mappedSections,
        },
      )
    }

    if (this.sharedKeys.length > 0) {
      return window.BAO.Cart.removeItems(this.sharedKeys, {
        sections: this.mappedSections,
      })
    }

    return window.BAO.Cart.removeItem(this.dataset.key, {
      sections: this.mappedSections,
    })
  }

  async onQuantityChange (e) {
    this.loading = true

    /** @type {string[]} */
    const keys = [this.dataset.key, ...this.sharedKeys]

    const quantityUpdates = keys.reduce((updates, key) => {
      updates[key] = e.detail.value

      return updates
    }, {})

    const state = await window.BAO.Cart.updateQuantities(quantityUpdates, {
      sections: this.mappedSections,
    })

    this.loading = false

    if ('sections' in state) this.renderSections(state.sections)
  }

  tryToRevealAddon () {
    const hasAlreadyBeenDismissed = this.dismissedAddons.includes(
      this.dataset.key,
    )

    if (hasAlreadyBeenDismissed) {
      return
    }

    this.els.addon?.element?.removeAttribute('aria-hidden')
  }

  dismissAddon () {
    window.sessionStorage.setItem(
      'push-cart:dismissed-addons',
      JSON.stringify([...this.dismissedAddons, this.dataset.key]),
    )

    this.els?.addon.element.setAttribute('aria-hidden', true)
  }

  renderSections (stateSections) {
    super.renderSections(stateSections)

    window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.AFTER_RENDER, {
      gwpRender: false,
    })
  }

  get mappedSections () {
    return this.sections.map(s => s.section)
  }

  get loading () {
    return this._loading
  }

  set loading (val) {
    this._loading = val

    if (this.inPushCart) {
      this.pushCart.loading = val

      if (this.externalEls.submit.exists) {
        this.externalEls.submit.element.disabled = val
      }
    } else if (this.onCartPage) {
      this.cart.loading = val
    }

    this.setAttribute('aria-busy', val.toString())
  }

  get pushCart () {
    return this.closest('push-cart')
  }

  get inPushCart () {
    return this.pushCart != null
  }

  get cart () {
    return this.closest('cart-items')
  }

  get onCartPage () {
    return this.cart != null
  }

  get dismissedAddons () {
    const defaultValue = []
    const dismissedAddons = window.sessionStorage.getItem(
      'push-cart:dismissed-addons',
    )

    if (!dismissedAddons) {
      return defaultValue
    }

    try {
      const value = JSON.parse(dismissedAddons)
      return value
    } catch (error) {
      return []
    }
  }

  get sharedKeys () {
    return (this.dataset.sharedIdKeys || '').split(',').filter(Boolean)
  }

  get isBespoke () {
    return this.dataset.isBespoke === 'true'
  }

  /**
   * @returns {string[]}
   */
  get bespokeKeys () {
    return this.dataset.bespokeKeys.split(',').filter(Boolean)
  }
}

if (!customElements.get('cart-item')) {
  customElements.define('cart-item', CartItem)
}
